import { useEffect, useRef, useState } from "react";
import { Paper, Snackbar, Alert } from "@mui/material";
import { Patient, updatePatient } from "../api/patients";
import DiseasesSelector from "./DiseasesSelector";

interface Props {
  patient: Patient;
  onPatientUpdated: (patient: Patient) => void;
  readOnly?: boolean;
}

export default function PatientDiseasesSection({ patient, onPatientUpdated, readOnly }: Props) {
  const [diseases, setDiseases] = useState<string[]>(patient.diseases || []);
  const [saving, setSaving] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [error, setError] = useState<string>("");
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setDiseases(patient.diseases || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.patient_id]);

  const handleChange = (next: string[]) => {
    setDiseases(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void save(next);
    }, 400);
  };

  const save = async (next: string[]) => {
    if (!patient.patient_id) return;
    setSaving(true);
    setError("");
    try {
      const updated = await updatePatient(patient.patient_id, { diseases: next });
      onPatientUpdated(updated);
      setToastOpen(true);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail === "No changes detected") {
        setToastOpen(true);
      } else {
        setError(typeof detail === "string" ? detail : "Ошибка сохранения");
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      <DiseasesSelector value={diseases} onChange={handleChange} disabled={readOnly || saving} />
      <Snackbar
        open={toastOpen}
        autoHideDuration={2000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setToastOpen(false)}>
          Сохранено
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
